import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { forwardRef, useCallback } from 'react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { noHitboxNeeded } from '@polestar/component-warehouse-react/helpers/style';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
export var LinkVariants;
(function (LinkVariants) {
    LinkVariants["default"] = "default";
    LinkVariants["list"] = "list";
    /**
     * @internal Used for the `<MarketSelector />, <NavigationDrawer />, <Footer />` components
     */
    LinkVariants["navigation"] = "navigation";
    /**
     * Used for the legal links in the `<Footer />` component
     */
    LinkVariants["legal"] = "legal";
})(LinkVariants || (LinkVariants = {}));
const Link = forwardRef(({ disabled = false, onClick, hideUnderline = false, variant = LinkVariants.default, isUnderlinedOnHover = false, ...props }, ref) => {
    const handleClick = useCallback((event) => {
        // Prevent default behaviour for disabled links
        if (!disabled) {
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
        }
    }, [onClick, disabled]);
    if (props.target && props.target === '_blank' && props.rel === undefined) {
        // eslint-disable-next-line no-console
        console.error('Please provide `Link.rel = "noopener" | "noreferrer"`, because of security considerations when using `target="_blank"`. More information at https://web.dev/external-anchors-use-rel-noopener/');
        return null;
    }
    const { isArabic } = useDirection();
    return (React.createElement(StyledLink, { ref: ref, ...aria({
            disabled,
        }), ...props, disabled: disabled, onClick: handleClick, hideUnderline: hideUnderline, variant: variant, isUnderlinedOnHover: isUnderlinedOnHover, isArabic: isArabic }));
});
Link.displayName = 'Link';
export default Link;
const StyledLink = styled('a', shouldNotForwardProps(true, 'disabled', 'hideUnderline', 'isUnderlinedOnHover', 'variant'))(({ theme, disabled, hideUnderline, isUnderlinedOnHover, variant = LinkVariants.default, isArabic = false, }) => {
    const stateStyles = theme.link.variants[variant].states;
    const noUnderline = hideUnderline || variant === LinkVariants.list || variant === LinkVariants.navigation;
    return css(css `
      color: ${stateStyles.enabled.textColor};
      transition:
        ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)},
        ${createCSSTransition('background-image', MotionEasings.default, MotionDurations.fast)};
      background-position: 0 80%;
      background-repeat: repeat-x;
      touch-action: manipulation;
      text-decoration: ${noUnderline ? 'none' : 'underline'};
      text-underline-offset: 0.125rem;
      -webkit-tap-highlight-color: transparent;

      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          color: ${stateStyles.hover.textColor};
          text-decoration: ${noUnderline ? 'none' : 'underline'};
        }
      }

      &:active {
        color: ${stateStyles.active.textColor};
      }
    `, isUnderlinedOnHover &&
        css `
        @media (hover: hover) {
          &:hover {
            text-decoration: underline;
          }
        }
      `, variant === LinkVariants.list &&
        css `
        display: flex;
        align-items: center;
        overflow-wrap: anywhere;
        padding: ${isArabic
            ? `${createCustomSpacing(10 / 16)} 0 ${createCustomSpacing(9 / 16)} 0`
            : `${createCustomSpacing(11 / 16)} 0`};
      `, variant === LinkVariants.navigation &&
        css `
        @media (pointer: coarse) {
          padding: ${createCustomSpacing(11 / 16)} 0;
        }
        padding: ${createCustomSpacing(5 / 16)} 0;
        display: flex;
        align-items: center;
        overflow-wrap: anywhere;
      `, disabled &&
        css `
        cursor: default;
        pointer-events: none;
        color: ${stateStyles.disabled.textColor};

        &:hover,
        &:active,
        &:focus {
          color: ${stateStyles.disabled.textColor};
        }
      `, noHitboxNeeded);
});
